/**
 * Keyring backup format.
 *
 * Extracted from keyring.worker.ts so unit tests can exercise the real
 * encode/decode paths without instantiating a SharedWorker.
 *
 * v1 layout:
 *   [1 byte version=1]
 *   [16 byte salt]
 *   [12 byte AES-GCM (Galois/Counter Mode) nonce]
 *   [4 byte fingerprint length, big-endian]
 *   [N byte fingerprint]    ← plaintext, for "is this the right backup" check
 *   [remaining bytes]        ← AEAD-encrypted seed string
 *
 * Key derivation: PBKDF2 (Password-Based Key Derivation Function 2) over
 * password with the salt. Backup import is rare, so high iteration count
 * (600k) is fine; tests may override via the iterations parameter.
 */

import { utf8ToBytes } from '@noble/hashes/utils';

export const BACKUP_VERSION = 1;
export const BACKUP_PBKDF2_ITERS = 600_000;

export interface ParsedBackup {
  version: number;
  fingerprint: string;
  seed: string;
}

/**
 * Internal: derive an AES-GCM key from password + salt.
 * Exported only for tests that want to verify the exact derivation.
 */
export async function passwordToBackupKey(
  password: string,
  salt: Uint8Array,
  iterations: number = BACKUP_PBKDF2_ITERS,
): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    utf8ToBytes(password) as BufferSource,
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Pack a seed and fingerprint into the v1 backup blob.
 *
 * @param iterations Override only for test speed. Production callers should
 *                   omit this and accept the default.
 */
export async function exportBackup(
  seed: string,
  fingerprint: string,
  password: string,
  iterations: number = BACKUP_PBKDF2_ITERS,
): Promise<Uint8Array> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const key = await passwordToBackupKey(password, salt, iterations);
  const fpBytes = utf8ToBytes(fingerprint);
  const seedBytes = utf8ToBytes(seed);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, seedBytes as BufferSource),
  );

  const out = new Uint8Array(1 + 16 + 12 + 4 + fpBytes.length + ct.length);
  let off = 0;
  out[off++] = BACKUP_VERSION;
  out.set(salt, off); off += 16;
  out.set(nonce, off); off += 12;
  new DataView(out.buffer).setUint32(off, fpBytes.length, false); off += 4;
  out.set(fpBytes, off); off += fpBytes.length;
  out.set(ct, off);
  return out;
}

/**
 * Decode a v1 backup blob and decrypt the seed.
 *
 * Throws if:
 *   - blob is too short
 *   - version byte is unrecognized
 *   - AEAD verification fails (wrong password, tampered blob)
 */
export async function importBackup(
  blob: Uint8Array,
  password: string,
  iterations: number = BACKUP_PBKDF2_ITERS,
): Promise<ParsedBackup> {
  if (blob.length < 1 + 16 + 12 + 4) {
    throw new Error('Backup too short');
  }
  let off = 0;
  const version = blob[off++]!;
  if (version !== BACKUP_VERSION) {
    throw new Error(`Unsupported backup version: ${version}`);
  }
  const salt = blob.slice(off, off + 16); off += 16;
  const nonce = blob.slice(off, off + 12); off += 12;
  const fpLen = new DataView(
    blob.buffer,
    blob.byteOffset,
    blob.byteLength,
  ).getUint32(off, false);
  off += 4;
  if (off + fpLen > blob.length) {
    throw new Error('Backup truncated: fingerprint length exceeds blob');
  }
  const fpBytes = blob.slice(off, off + fpLen); off += fpLen;
  const ct = blob.slice(off);
  const fingerprint = new TextDecoder().decode(fpBytes);

  const key = await passwordToBackupKey(password, salt, iterations);
  let seedBytes: ArrayBuffer;
  try {
    seedBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key, ct);
  } catch {
    throw new Error('Backup decryption failed (wrong password?)');
  }
  const seed = new TextDecoder().decode(seedBytes);
  return { version, fingerprint, seed };
}
